import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

interface DirectoristListing {
  id: number;
  name: string;
  categories: { id: number; name: string }[];
  locations: { id: number; name: string }[];
  images: { src: string }[];
  average_rating: number;
  rating_count: number;
}

export default function VendorsPage() {
  const navigate = useNavigate();
  const locationHook = useLocation();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [providers, setProviders] = useState<DirectoristListing[]>([]);
  const [loading, setLoading] = useState(true);

  // Read URL params and prefill filters
  useEffect(() => {
    const params = new URLSearchParams(locationHook.search);

    const query = params.get('search') || '';
    setSearchQuery(query);

    const category = params.get('category') || 'all';
    setSelectedSpecialty(category);

    const locationParam = params.get('location') || 'all';
    setSelectedLocation(locationParam);
  }, [locationHook.search]);

  // Fetch providers from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch('https://therevulist.com/wp-json/directorist/v1/listings');
        const json = await res.json();
        setProviders(json);
      } catch (err) {
        console.error('API Error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSearch = (e: React.FormEvent) => e.preventDefault();

  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const stars = [];
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) stars.push(<i key={i} className="ri-star-fill text-yellow-400" />);
      else if (i === fullStars && hasHalfStar) stars.push(<i key={i} className="ri-star-half-fill text-yellow-400" />);
      else stars.push(<i key={i} className="ri-star-line text-yellow-400" />);
    }
    return stars;
  };

  const filtered = providers.filter((item) => {
    const nameMatch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const specialtyMatch =
      selectedSpecialty === 'all' ||
      item.categories.some((cat) => cat.name.toLowerCase() === selectedSpecialty.toLowerCase());
    const locationMatch =
      selectedLocation === 'all' ||
      item.locations.some((loc) => loc.name.toLowerCase() === selectedLocation.toLowerCase());
    return nameMatch && specialtyMatch && locationMatch;
  });

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-grow">
        {/* Hero + Filters */}
        <section className="bg-gradient-to-br from-teal-600 to-cyan-600 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <h1 className="text-4xl font-bold mb-4">Find Your Healthcare Provider</h1>
            <p className="text-xl text-teal-50 max-w-3xl mx-auto mb-8">
              Browse verified healthcare vendors sourced from Directorist.
            </p>

            <form onSubmit={handleSearch} className="max-w-4xl mx-auto">
              <div className="bg-white rounded-lg shadow-xl p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <input
                  type="text"
                  placeholder="Search vendors..."
                  className="w-full p-3 border rounded-lg text-gray-700"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />

                <select
                  className="w-full p-3 border rounded-lg text-gray-700"
                  value={selectedSpecialty}
                  onChange={(e) => setSelectedSpecialty(e.target.value)}
                >
                  <option value="all">All Specialties</option>
                  {[...new Set(providers.flatMap((p) => p.categories.map((c) => c.name)))].map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>

                <select
                  className="w-full p-3 border rounded-lg text-gray-700"
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                >
                  <option value="all">All Locations</option>
                  {[...new Set(providers.flatMap((p) => p.locations.map((l) => l.name)))].map((loc) => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
              </div>
            </form>
          </div>
        </section>

        {/* Provider Cards */}
        <div className="max-w-7xl mx-auto px-4 py-12">
          {loading ? (
            <div className="text-center text-lg font-medium">Loading providers...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-lg text-gray-600">No providers found.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((item) => {
                const image = item.images.length ? item.images[0].src : 'https://placehold.co/400x400?text=No+Image';
                const specialty = item.categories[0]?.name || 'N/A';
                const location = item.locations[0]?.name || 'Unknown';

                return (
                  <div key={item.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition border">
                    <img src={image} alt={item.name} className="h-48 w-full object-cover rounded-lg mb-4" />
                    <h3 className="text-xl font-semibold mb-1">{item.name}</h3>
                    <p className="text-sm text-gray-600 mb-2">{specialty}</p>
                    <p className="text-sm text-gray-500 mb-3">{location}</p>
                    <div className="flex items-center mb-4">{renderStars(item.average_rating)}
                      <span className="ml-2 text-gray-600 text-sm">({item.rating_count})</span>
                    </div>
                    <button
                      className="w-full bg-teal-600 hover:bg-teal-700 text-white py-2 rounded-lg transition"
                      onClick={() => navigate(`/provider/${item.id}`)}
                    >
                      View Profile
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
